import { Injectable, inject } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs';

import { BaseEffects } from '../../../base.effects';
import { FORGOT_PASSWORD_DIALOG, FORGOT_PASSWORD_SUBMIT_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { NotificationActions } from '../../notifications/notification.actions';
import { SpinnerActions } from '../../spinner/spinner-state.actions';
import { ForgotPasswordStatus } from '../services/auth.types';

import { ForgotPasswordActions } from './forgot-password.actions';

const apiV4 = 'api/v4';

@Injectable()
export class ForgotPasswordEffects extends BaseEffects {
  private readonly openForgotPassword = inject(FORGOT_PASSWORD_DIALOG);
  private readonly openForgotPasswordSubmit = inject(FORGOT_PASSWORD_SUBMIT_DIALOG);

  openForgotPasswordDialog$ = this.exhaustMapEffect({
    action: ForgotPasswordActions.openDialog,
    exhaustMapFn: ({ email, workflow }) =>
      this.openForgotPassword({
        data: { email, workflow },
      })
        .afterClosed()
        .pipe(
          map(({ email, hasCode }) => {
            if (email) return ForgotPasswordActions.submitEmailForReset({ workflow, email });
            if (hasCode) return ForgotPasswordActions.openResetDialog({ workflow, email });
            return ForgotPasswordActions.cancelDialog();
          })
        ),
  });

  submitForgotPasswordRequest_callApi$ = this.switchMapEffect({
    action: ForgotPasswordActions.submitEmailForReset,
    switchMapFn: ({ email, workflow }) =>
      this.http.patch<{ status: ForgotPasswordStatus }>(`${apiV4}/Person/Forgot`, { email }).pipe(
        map(({ status }) => ForgotPasswordActions.resetEmailResponse({ status, email, workflow })),
        catchError((error: unknown) => of(ForgotPasswordActions.submitEmailRejected({ error })))
      ),
  });

  submitPasswordRejected_showError$ = this.mapEffect({
    action: ForgotPasswordActions.submitEmailRejected,
    mapFn: () => NotificationActions.notify({ message: 'Something went wrong. Please try again.' }),
  });

  /**
   * One next step on screen, for every address submitted.
   *
   * `/Person/Forgot` used to answer with what it had found -- no account, an unaccepted invite, an
   * account ready to reset, an account that first had to verify its identity -- and this file
   * turned each of those into something different on screen, so anyone could use the reset dialog
   * to learn who holds a Ninety account. The endpoint now answers every address identically and
   * emails a code to whichever ones it can, so the code dialog opens unconditionally: a real user
   * has a code waiting for them, and someone probing an address they do not own learns nothing
   * from being asked for a code that never arrives. DEV-27773.
   */
  submitForgotPasswordResponse_openResetDialog$ = this.mapEffect({
    action: ForgotPasswordActions.resetEmailResponse,
    mapFn: ({ email, workflow }) => ForgotPasswordActions.openResetDialog({ workflow, email }),
  });

  openResetDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.openResetDialog),
      exhaustMap(({ workflow, email, isCodeExpired }) =>
        this.openForgotPasswordSubmit({ data: { email, workflow, isCodeExpired: isCodeExpired ?? false } })
          .afterClosed()
          .pipe(
            map(output =>
              output.cancelled
                ? ForgotPasswordActions.cancelDialog()
                : ForgotPasswordActions.submitNewPassword({
                    workflow: workflow,
                    email: output.email,
                    code: output.code,
                    password: output.password,
                  })
            )
          )
      )
    )
  );

  resetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.resetPasswordSuccess),
      map(() =>
        NotificationActions.notify({
          message: 'Password successfully changed!',
          duration: 5000,
        })
      )
    )
  );

  submitNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.submitNewPassword),
      switchMap(({ workflow, email, code, password }) =>
        this.http
          .patch<string>(`${apiV4}/Person/Forgot/Submit`, {
            email,
            code,
            newPassword: password,
          })
          .pipe(
            map(() => ForgotPasswordActions.resetPasswordSuccess({ workflow })),
            catchError((error: unknown) => of(ForgotPasswordActions.resetPasswordFailed({ workflow, email, error })))
          )
      )
    )
  );

  /**
   * One message for every rejection.
   *
   * `/Person/Forgot/Submit` used to say which way a submission failed, and this file turned an
   * expired code, an exceeded attempt limit and everything else into three different messages.
   * Cognito answers ExpiredCodeException when no reset code was ever requested, so those answers
   * told anyone who asked which addresses hold accounts -- without needing a code to ask with. The
   * endpoint now rejects everything identically, so there is one thing to say, and it names the
   * action that resolves every case. DEV-27773.
   */
  resetPasswordFailed_showError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.resetPasswordFailed),
      map(() =>
        NotificationActions.showError({
          message: 'That code did not work or has expired. Request a new code and try again.',
          title: '',
        })
      )
    )
  );

  resetPasswordFailed_reOpenDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.resetPasswordFailed),
      map(({ email, workflow }) => ForgotPasswordActions.openResetDialog({ email, workflow }))
    )
  );

  showSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ForgotPasswordActions.submitEmailForReset, ForgotPasswordActions.submitNewPassword),
      map(({ type }) => SpinnerActions.startPrimary({ source: type }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ForgotPasswordActions.submitEmailRejected,
        ForgotPasswordActions.resetEmailResponse,
        ForgotPasswordActions.resetPasswordSuccess,
        ForgotPasswordActions.resetPasswordFailed,
        ForgotPasswordActions.cancelDialog
      ),
      map(({ type }) => SpinnerActions.stopPrimary({ source: type }))
    )
  );
}
